/**
 * «Деньги» вокруг вещей (концепт 26.10.2026, доработка 27.10.2026,
 * docs/design/goals-tab-2026-10-27).
 *
 * В «Деньги» приходят за конкретной вещью — «моя карта», «мой автокредит» — и
 * чтобы что-то с ней сделать. Поэтому вместо вкладок — обзор: капитал,
 * «Мои деньги» и «Долги». Расписание платежей — на главной, здесь его нет:
 * второй календарь повторял бы «Ближайшие платежи» (27.10.2026). Тап по вещи открывает её
 * экран: сумма крупно, действия кружками, что с ней связано.
 *
 * Обзор — плитками или списком, на выбор человека: плитки быстрее читаются,
 * список вмещает больше счетов на экран. Выбор помнится на устройстве.
 *
 * Цели здесь только строкой «на цели»: у них своя вкладка меню.
 */
import { useState, type ReactNode } from 'react';
import {
  ZERO, add, formatMoney, kop,
  type AvailableResult, type Loan, type Money, type PlainDate,
} from '@skoka/core';
import type { AppState, Tx } from './store.js';
import { Icon } from './icon.js';
import { AccountGlyph, IconGlyph } from './account-glyph.js';
import { checkedText } from './adjust.js';
import { goalAccountId, goalsOnAccount } from './goal-account.js';
import { accountGroups } from './account-groups.js';
import { nextPaymentInstance } from './next-payment.js';
import { dayMonth, plural } from './text.js';

export type MoneyLayout = 'tiles' | 'list';
const LAYOUT_KEY = 'skoka.money.layout';

/** Свёрнут ли раздел — помнится на устройстве; по умолчанию открыт. */
function useFold(key: string): [boolean, () => void] {
  const k = `skoka.money.fold.${key}`;
  const [folded, setFolded] = useState<boolean>(() => {
    try { return localStorage.getItem(k) === '1'; } catch { return false; }
  });
  return [folded, () => setFolded((f) => { try { localStorage.setItem(k, f ? '0' : '1'); } catch { /* только на сейчас */ } return !f; })];
}

/** К разделу обзора: заголовок встаёт под шапку экрана. */
const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 12, behavior: 'smooth' });
};

/** Плитки или список — помнится на устройстве; нет хранилища — плитки. */
export function useMoneyLayout(): [MoneyLayout, (l: MoneyLayout) => void] {
  const [layout, setLayout] = useState<MoneyLayout>(() => {
    try { return localStorage.getItem(LAYOUT_KEY) === 'list' ? 'list' : 'tiles'; } catch { return 'tiles'; }
  });
  return [layout, (l) => { setLayout(l); try { localStorage.setItem(LAYOUT_KEY, l); } catch { /* без памяти — только на сейчас */ } }];
}

const MONTHS_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

/** Значок кредита по названию: ипотека — дом, авто — машина. */
export function loanGlyph(l: Pick<Loan, 'name' | 'kind'>): string {
  if (/ипотек/i.test(l.name)) return 'house';
  if (/авто|машин/i.test(l.name)) return 'car';
  if (l.kind === 'installment') return 'bag-shopping';
  return 'hand-holding-dollar';
}

/**
 * Сколько выплачено — оценкой: первоначальной суммы кредит не хранит, она
 * выводится из платежа, ставки и срока по договору (аннуитет). Нет срока или
 * даты выдачи — полоски нет, а не выдуманный процент.
 */
export function loanPaidPct(l: Loan): number | null {
  if (!l.openedAt || !l.endDate || l.payment <= ZERO) return null;
  const n = Math.round((Date.parse(l.endDate) - Date.parse(l.openedAt)) / (30.44 * 864e5));
  if (n <= 0) return null;
  const r = l.ratePct / 100 / 12;
  const start = r > 0 ? l.payment * (1 - Math.pow(1 + r, -n)) / r : l.payment * n;
  if (start <= l.principal) return 0;
  return Math.max(0, Math.min(100, Math.round((1 - l.principal / start) * 100)));
}

const yearOf = (d?: PlainDate) => (d ? d.slice(0, 4) : null);
/** Ссылка на прежний блок «Долги»: очередь есть, только когда долгов больше одного. */
export const queueTitle = (n: number): string => (n > 1 ? 'Что гасить первым' : 'Проценты и досрочно');

interface DebtView { key: string; name: string; glyph: ReactNode; amount: Money; pct: number | null; sub: string; warn: boolean; open: () => void }

export function MoneyOverview({ state, result, ask, onAccount, onLoan, onCard, onPlans, onAddAccount, onAddLoan, onGoals, onOrder, onArchive, onQueue, onCheck }: {
  state: AppState; result: AvailableResult; ask?: ReactNode;
  onAccount: (id: string) => void; onLoan: (id: string) => void; onCard: (id: string) => void;
  /** «Доход и платежи» — списки, где их настраивают. */
  onPlans: () => void;
  onAddAccount: () => void; onAddLoan: () => void; onGoals: () => void;
  /** «Порядок счетов», «Архив», «Что гасить первым» — то, что было в прежних «Счетах» и «Платежах». */
  onOrder: () => void; onArchive: () => void; onQueue: () => void;
  /** Сверить остатки: «N счетов давно не сверяли». */
  onCheck: () => void;
}) {
  const [layout, setLayout] = useMoneyLayout();
  const [ownFolded, foldOwn] = useFold('own');
  const [debtFolded, foldDebt] = useFold('debts');
  const live = state.accounts.filter((a) => !a.archived);
  const archived = state.accounts.filter((a) => a.archived).length;
  // Порядок — тот же, что в «Порядке счетов»: группы и счета внутри них.
  const ordered = accountGroups(live, state.accountGroups).flatMap((g) => g.accounts);
  const own = ordered.filter((a) => a.kind !== 'credit_card');
  // Кредитка — в «Долгах» всегда, и без долга: иначе её не найти.
  const cards = ordered.filter((a) => a.kind === 'credit_card');
  const loans = state.loans.filter((l) => l.kind !== 'credit_card' && l.principal > ZERO);
  // Капитал — как прежде: все счета минус кредиты. Переплата по кредитке — свои деньги, её долг — долг.
  const ownSum = add(ZERO, ...own.map((a) => a.balance), ...cards.map((a) => (a.balance > ZERO ? a.balance : ZERO)));
  const reservedOn = (id: string): Money => add(ZERO, ...state.reserves.filter((r) => r.accountId === id).map((r) => r.amount));
  const debts: DebtView[] = [
    ...loans.map((l) => {
      const pct = loanPaidPct(l);
      const parts = [pct !== null ? `выплачено ${pct}%` : `${String(l.ratePct).replace('.', ',')}% годовых`, yearOf(l.endDate) ? `до ${yearOf(l.endDate)}` : ''];
      return { key: l.id, name: l.name, glyph: <IconGlyph icon={loanGlyph(l)} color="violet" />, amount: l.principal, pct,
        sub: parts.filter(Boolean).join(' · '), warn: false, open: () => onLoan(l.id) };
    }),
    ...cards.map((a) => {
      const st = result.credit.find((c) => c.accountId === a.id);
      const when = st ? dayMonth(st.paymentDate, state.today) : '';
      const sub = !st ? 'условия не заданы — нажми, чтобы задать'
        : st.missing > ZERO && !st.settled ? `не хватает ${formatMoney(st.missing)} к ${when}`
          : st.needForGrace > ZERO && !st.settled ? `платёж ${when} · отложено`
            : a.balance < ZERO ? `доступно ${formatMoney(st.availableLimit)}` : `долга нет · доступно ${formatMoney(st.availableLimit)}`;
      // Полоски у кредитки нет: у кредитов она — сколько выплачено, а «занято от лимита» читалось бы так же.
      return { key: a.id, name: a.name, glyph: <AccountGlyph account={a} />, amount: a.balance < ZERO ? kop(-a.balance) : ZERO, pct: null,
        sub, warn: !st || (st.missing > ZERO && !st.settled), open: () => onCard(a.id) };
    }),
  ];
  const debtSum = add(ZERO, ...debts.map((d) => d.amount));
  const onGoalsSum = add(ZERO, ...state.goals
    .filter((g) => (g.status === 'active' || g.status === 'paused') && own.some((a) => a.id === goalAccountId(state, g)))
    .map((g) => g.currentAmount));
  const incomes = state.incomeStreams.filter((x) => x.isActive).length;
  const payments = state.obligations.filter((x) => x.isActive).length;

  /*
    Давность сверки — точкой у значка и одной строкой над списком
    (02.11.2026): жёлтое «сверен 18 сентября · 6 недель» в каждой плитке
    пестрило и забивало остальное. Дата — на экране самого счёта.
  */
  const stale = (a: AppState['accounts'][number]): boolean => checkedText(state, a.id)?.warn ?? false;
  const staleCount = own.filter(stale).length;
  const accSub = (a: AppState['accounts'][number]): { text: string; warn: boolean } => {
    const goals = goalsOnAccount(state, a.id);
    if (goals.length > 0) return { text: goals.map((g) => g.name).join(', '), warn: false };
    const held = reservedOn(a.id);
    if (held > ZERO) return { text: `закреплено ${formatMoney(held)}`, warn: false };
    return { text: a.isLiquid ? 'для трат' : 'не для трат', warn: false };
  };

  const tiles = layout === 'tiles';
  return (
    <>
      <header className="pgtitle mn-title"><h1>Деньги</h1>
        <span className="mn-tools">
          <span className="mn-switch" role="group" aria-label="Вид">
            <button className={tiles ? 'on' : ''} aria-pressed={tiles} aria-label="Плитками" onClick={() => setLayout('tiles')}><Icon name="grid-2" size={15} /></button>
            <button className={!tiles ? 'on' : ''} aria-pressed={!tiles} aria-label="Списком" onClick={() => setLayout('list')}><Icon name="list" size={15} /></button>
          </span>
          {ask}
        </span>
      </header>

      {/*
        Капитал — одной карточкой (27.10.2026): сумма крупно, под ней полоска
        «свои / долги» — сразу видно, откуда минус. Капитал не красный: с
        ипотекой минус нормален.
      */}
      <section className="blk mn-cap">
        <span className="mn-cap-l">Капитал</span>
        <b className="mn-cap-v num">{formatMoney(kop(ownSum - debtSum))}</b>
        {ownSum + debtSum > 0 && (
          <span className="mn-cap-bar" aria-hidden="true">
            {ownSum > 0 && <i className="own" style={{ flexGrow: ownSum }} />}
            {debtSum > 0 && <i className="debt" style={{ flexGrow: debtSum }} />}
          </span>
        )}
        {/* Свои и долги — кнопками к своим разделам: при десяти счетах долги далеко внизу. */}
        <span className="mn-cap-legend">
          <button onClick={() => { if (ownFolded) foldOwn(); scrollToSection('mn-own'); }}>
            <span><i className="own" />Свои<Icon name="chevron-right" size={10} /></span><b className="num">{formatMoney(ownSum)}</b></button>
          <button onClick={() => { if (debtFolded) foldDebt(); scrollToSection('mn-debts'); }}>
            <span><i className="debt" />Долги<Icon name="chevron-right" size={10} /></span><b className="num">{formatMoney(debtSum)}</b></button>
        </span>
        {/* Часть своих, уже занятая целями: деньги целей лежат на счетах и в капитал не прибавляются. */}
        {onGoalsSum > ZERO && (
          <button className="mn-cap-goals" onClick={onGoals}>
            <Icon name="bullseye-arrow" size={14} /><span>Из своих на цели</span>
            <b className="num">{formatMoney(onGoalsSum)}</b><Icon name="chevron-right" size={12} />
          </button>
        )}
      </section>

      {/*
        Расписание платежей и приходов — на главной («Ближайшие платежи» и «Все»).
        Здесь — только вход туда, где доход и платежи настраивают.
      */}
      <button className="blk mn-plans" onClick={onPlans}>
        <IconGlyph icon="calendar-check" color="blue" />
        <span className="nm"><b>Доход и платежи</b>
          <span>{plural(incomes, 'источник', 'источника', 'источников')} · {plural(payments, 'платёж', 'платежа', 'платежей')}</span></span>
        <Icon name="chevron-right" size={13} />
      </button>


      {/* Заголовок одной строкой: название, сумма раздела и «+» — при длинном списке до конца листать не нужно. */}
      <div className="mn-h" id="mn-own">
        <button className="mn-fold" onClick={foldOwn} aria-expanded={!ownFolded}>
          <b>Мои деньги</b><Icon name={ownFolded ? 'chevron-down' : 'chevron-up'} size={13} />
          {ownFolded && <span className="mn-fold-n">{plural(own.length, 'счёт', 'счёта', 'счетов')}</span>}</button>
        <span className="num">{formatMoney(ownSum)}</span>
        <button className="icobtn sm addbtn" onClick={onAddAccount} aria-label="Добавить счёт"><Icon name="plus" size={15} /></button></div>
      {!ownFolded && staleCount > 0 && (
        <button className="mn-stale-row" onClick={onCheck}>
          <i className="mn-dot" aria-hidden="true" />
          <span>{plural(staleCount, 'счёт', 'счёта', 'счетов')} давно не сверяли</span>
          <span className="mn-stale-go">Сверить<Icon name="chevron-right" size={11} /></span>
        </button>
      )}
      {ownFolded ? null : tiles ? (
        <div className="tiles2">
          {own.map((a) => {
            const s = accSub(a);
            return (
              <button className="wt" key={a.id} onClick={() => onAccount(a.id)}>
                <span className="wt-top"><span className="mn-glyph"><AccountGlyph account={a} size="s" />{stale(a) && <i className="mn-dot" aria-label="давно не сверен" />}</span><b>{a.name}</b></span>
                <span className={`wt-sum num${a.balance < ZERO ? ' bad' : ''}`}>{formatMoney(a.balance)}</span>
                <span className={`wt-sub${s.warn ? ' warn' : ''}`}>{s.text}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <section className="blk mn-list">
          {own.map((a) => {
            const s = accSub(a);
            return (
              <button className="li w" key={a.id} onClick={() => onAccount(a.id)}>
                <span className="mn-glyph"><AccountGlyph account={a} />{stale(a) && <i className="mn-dot" aria-label="давно не сверен" />}</span>
                <div className="nm"><b>{a.name}</b><span className={s.warn ? 'warn' : ''}>{s.text}</span></div>
                <div className={`am num${a.balance < ZERO ? ' bad' : ''}`}>{formatMoney(a.balance)}</div>
              </button>
            );
          })}
        </section>
      )}
      {/* Редкое — тихими ссылками под разделом: «Порядок» нужен, когда счетов больше одного, «Архив» — когда он есть. */}
      {!ownFolded && (live.length > 1 || archived > 0) && (
        <div className="mn-links">
          {live.length > 1 && <button onClick={onOrder}><Icon name="arrow-up-arrow-down" size={12} />Порядок счетов</button>}
          {archived > 0 && <button onClick={onArchive}><Icon name="box-archive" size={12} />Архив · {archived}</button>}
        </div>
      )}

      <div className="mn-h" id="mn-debts">
        <button className="mn-fold" onClick={foldDebt} aria-expanded={!debtFolded}>
          <b>Долги</b><Icon name={debtFolded ? 'chevron-down' : 'chevron-up'} size={13} />
          {debtFolded && <span className="mn-fold-n">{plural(debts.length, 'долг', 'долга', 'долгов')}</span>}</button>
        <span className="num">{formatMoney(debtSum)}</span>
        <button className="icobtn sm addbtn" onClick={onAddLoan} aria-label="Добавить кредит"><Icon name="plus" size={15} /></button></div>
      {debtFolded ? null : tiles ? (
        <div className="tiles2">
          {debts.map((d) => (
            <button className="wt" key={d.key} onClick={d.open}>
              <span className="wt-top">{d.glyph}<b>{d.name}</b></span>
              <span className="wt-sum num bad">{formatMoney(d.amount)}</span>
              {d.pct !== null && <span className="wt-bar"><i style={{ width: `${d.pct}%` }} /></span>}
              <span className={`wt-sub${d.warn ? ' warn' : ''}`}>{d.sub}</span>
            </button>
          ))}
        </div>
      ) : (
        <section className="blk mn-list">
          {debts.map((d) => (
            <button className="li w" key={d.key} onClick={d.open}>
              {d.glyph}
              <div className="nm"><b>{d.name}</b><span className={d.warn ? 'warn' : ''}>{d.sub}</span></div>
              <div className="am num">{formatMoney(d.amount)}</div>
            </button>
          ))}
        </section>
      )}
      {/* Прежний блок «Долги» — проценты в год и в день, возраст долга, досрочное; при нескольких долгах — ещё и очередь. */}
      {!debtFolded && debts.some((d) => d.amount > ZERO) && (
        <div className="mn-links">
          <button onClick={onQueue}><Icon name="list-ol" size={12} />{queueTitle(debts.filter((d) => d.amount > ZERO).length)}</button>
        </div>
      )}
    </>
  );
}

/**
 * Экран кредита: остаток крупно, сколько выплачено, действия — Оплатить,
 * Досрочно, Остаток, Условия; ближайший платёж и история платежей. Кнопки
 * открывают те листы, что уже есть.
 */
export function LoanScreen({ state, loanId, onBack, onPay, onPrepay, onBalance, onTerms, onTx }: {
  state: AppState; loanId: string; onBack: () => void;
  onPay: () => void; onPrepay: () => void; onBalance: () => void; onTerms: () => void; onTx: (id: string) => void;
}) {
  const l = state.loans.find((x) => x.id === loanId);
  if (!l) return null;
  const pct = loanPaidPct(l);
  const ob = state.obligations.find((o) => o.id === l.obligationId);
  const next = l.principal > ZERO && ob ? nextPaymentInstance(state, ob.id) : null;
  const from = state.accounts.find((a) => a.id === (ob?.accountId ?? l.accountId));
  // Одной строкой под суммой: по центру перенос выглядел бы рваным.
  const end = l.endDate ? `до ${MONTHS_GEN[Number(l.endDate.slice(5, 7)) - 1]} ${l.endDate.slice(0, 4)}` : null;
  const sub = [pct !== null ? `выплачено ${pct}%` : null, `${String(l.ratePct).replace('.', ',')}%`, end].filter(Boolean).join(' · ');
  const history = state.transactions.filter((t: Tx) => (t.link?.kind === 'obligation' && t.link.obligationId === l.obligationId)
    || (t.link?.kind === 'loan' && t.link.loanId === l.id))
    .sort((a, b) => b.occurredDate.localeCompare(a.occurredDate)).slice(0, 6);
  const acts: [string, string, () => void, boolean?][] = [
    ['check', 'Оплатить', onPay, true], ['hand-holding-dollar', 'Досрочно', onPrepay], ['circle-info', 'Условия', onTerms],
  ];
  return (
    <>
      <div className="obj-top">
        <button className="obj-back" onClick={onBack}><Icon name="chevron-left" size={14} />Деньги</button>
        {/* Как у счёта: карандаш у суммы — остаток долга; условия — кнопкой «Условия». */}
        <div className="obj-head"><IconGlyph icon={loanGlyph(l)} color="violet" size="s" /><b>{l.name}</b></div>
        <div className="obj-sumrow"><span className="obj-sum num">{formatMoney(l.principal)}</span>
          <button className="penbtn" onClick={onBalance} aria-label="Поправить остаток долга"><Icon name="pen" size={14} /></button></div>
        {pct !== null && <div className="obj-bar"><i style={{ width: `${pct}%` }} /></div>}
        <div className="obj-sub">{sub}</div>
        <div className="obj-acts">
          {acts.map(([icon, label, go, main]) => (
            <button key={label} className={main ? 'main' : ''} onClick={go}><span><Icon name={icon} size={19} /></span>{label}</button>
          ))}
        </div>
      </div>
      <section className="blk">
        <div className="blkhead"><b>Ближайший платёж</b></div>
        {next ? <div className="li">
          <IconGlyph icon="calendar-check" color="blue" />
          <div className="nm"><b>{dayMonth(next.scheduledDate, state.today)} · {formatMoney(next.unpaid)}</b>
            <span>{from ? `счёт: ${from.name}` : 'счёт не выбран'}</span></div>
        </div> : <p className="obj-none">{l.principal <= ZERO ? 'Кредит погашен.' : ob ? 'Платежей по расписанию больше нет.' : 'График платежей не задан.'}</p>}
      </section>
      <div className="obj-h"><b>История</b></div>
      {history.length === 0 ? <p className="obj-none">Платежей по этому кредиту пока не записано.</p> : (
        <section className="blk">
          {history.map((t) => (
            <button className="li w" key={t.id} onClick={() => onTx(t.id)}>
              <IconGlyph icon={loanGlyph(l)} color="violet" />
              <div className="nm"><b>{t.link?.kind === 'loan' ? 'Досрочно' : 'Платёж по графику'}</b>
                <span>{dayMonth(t.occurredDate, state.today)}{state.accounts.find((a) => a.id === t.accountId) ? ` · ${state.accounts.find((a) => a.id === t.accountId)!.name}` : ''}</span></div>
              <div className="am num">−{formatMoney(t.amount)}</div>
            </button>
          ))}
        </section>
      )}
    </>
  );
}
